import React, { Suspense } from "react";
import { Backdrop, CssBaseline } from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { Redirect, Route, Switch } from "react-router-dom";

import "./App.css";
import { ReducerStates, Views } from "../../helpers/constants";
import UpdateSubscriptions from "../dashboard/UpdateSubscriptions";
import Login from "../login/Login";
import LoginConfirmation from "../login/LoginConfirmation";
import SubscriptionConfirmation from "../dashboard/SubscriptionConfirmation";
import ViewSubscriptions from "../dashboard/ViewSubscriptions";
import AccessExpired from "../dashboard/AccessExpired";
import PrivateRoute from "./PrivateRoute";
import { useSelector } from "react-redux";
import { IAppState } from "../../helpers";
import { LoadingIndicator } from "../common/LoadingIndicator";
import LoadSubscription from "../login/LoadSubscription";
import ViewBillingManagers from "../dashboard/ViewBillingManagers";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    logged: {
      minHeight: `calc(100vh - ${theme.mixins.toolbar.minHeight})`,
      backgroundColor: theme.palette.light.main,

      overflow: "hidden",
    },
    notLogged: {
      minHeight: `100vh`,
      backgroundColor: theme.palette.light.main,
      display: "flex",
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: "#fff",
    },
  })
);

/**
 * App component
 *
 * @returns renders the app
 */
const App = () => {
  /* ---- Hooks ---- */
  const classes = useStyles();
  /* ---- Selectors ---- */
  const loginEmailSent: boolean = useSelector(
    (state: IAppState) => state.loginState.loginEmailSent
  );
  const dashboardError = useSelector(
    (state: IAppState) => state.dashboardState.errorCode
  );
  const appState = useSelector((state: IAppState) => state.appState.state);
  const isLogged = useSelector((state: IAppState) => state.loginState.isLogged);
  const isLoading = useSelector((state: IAppState) => {
    return (
      state.loginState.state === ReducerStates.PENDING ||
      state.dashboardState.state === ReducerStates.PENDING
    );
  });

  const initialLoadFinished = appState === ReducerStates.SUCCEEDED;

  return (
    <React.Fragment>
      {initialLoadFinished && (
        <React.Fragment>
          <CssBaseline />
          <div className={classes.notLogged} id="main">
            <Switch>
              <Route exact path={Views.ROOT}>
                {isLogged ? (
                  <Redirect to={Views.SUBSCRIPTION} />
                ) : (
                  <Redirect to={Views.LOGIN} />
                )}
              </Route>
              <PrivateRoute
                path={Views.SUBSCRIPTION}
                component={ViewSubscriptions}
                exact
              />
              <PrivateRoute
                path={Views.SUBSCRIPTION_MANAGEMENT}
                component={UpdateSubscriptions}
                exact
              />
              <PrivateRoute
                path={Views.SUBSCRIPTION_CONFIRMATION}
                component={SubscriptionConfirmation}
                exact
              />
              <PrivateRoute
                path={Views.BILLING_MANAGERS}
                component={ViewBillingManagers}
                exact
              />
              <Route exact path={Views.LOGIN}>
                <Login />
              </Route>
              <Route exact path={Views.LOGIN_LOAD_SUBSCRIPTION}>
                <LoadSubscription />
              </Route>
              <Route exact path={Views.LOGIN_CONFIRMATION}>
                {loginEmailSent ? (
                  <LoginConfirmation />
                ) : (
                  <Redirect to={Views.LOGIN} />
                )}
              </Route>
              <Route exact path={Views.ACCESS_EXPIRED}>
                {!dashboardError ? (
                  <Redirect to={Views.SUBSCRIPTION} />
                ) : (
                  <AccessExpired />
                )}
              </Route>
              <Route path="*">
                <Redirect to={Views.LOGIN} />
              </Route>
            </Switch>
            <Backdrop className={classes.backdrop} open={isLoading}>
              <LoadingIndicator />
            </Backdrop>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default function WrappedApp() {
  return (
    <Suspense fallback={<div>Loading... </div>}>
      <App />
    </Suspense>
  );
}
